// Process-aligned 10-step workflow for a lead/order.
// `tab` maps a step to the LeadDrawer StepFlow tab key (null = open drawer default).
export const WORKFLOW_STEPS = [
  { key: "call", label: "Call Update", tab: null, done: (l) => l.status && l.status !== "pending" },
  { key: "rfq", label: "Send RFQ", tab: null, done: (l) => !!l.rfq_sent },
  { key: "quote", label: "Make Quotation", tab: "quotation", done: (l) => l.quote_status && l.quote_status !== "none" },
  { key: "lr", label: "Generate LR", tab: "lr", done: (l) => !!l.lr_number },
  { key: "vendor_docs", label: "Vendor Docs", tab: "vendor_docs", done: (l) => l.vendor_docs_status && l.vendor_docs_status !== "requested" },
  { key: "verify_docs", label: "Verify Vendor Docs", tab: "vendor_docs", done: (l) => l.vendor_docs_status === "verified" },
  { key: "ops", label: "Operations", tab: "ops", done: (l) => !!(l.driver_mobile || "").trim() || !!l.load_type },
  { key: "gen_docs", label: "Generate Docs", tab: "documents", done: (l) => l.order_state === "confirmed_dispatched" },
  { key: "delivery", label: "Delivery", tab: "delivery", done: (l) => !!l.delivered_at || l.shipment_status === "delivered" || l.status === "done" },
  { key: "expenses", label: "Update Expenses", tab: "expenses", done: (l) => l.status === "done" },
];

// Index of the current (first not-done) step. Equals WORKFLOW_STEPS.length when all done.
export const currentStepIndex = (lead) => {
  const idx = WORKFLOW_STEPS.findIndex((s) => !s.done(lead));
  return idx === -1 ? WORKFLOW_STEPS.length : idx;
};

// Action-oriented status: shows the next logical action for the lead, colour-coded.
// Clicking opens the relevant step in the Lead drawer (one-tap action).
export const leadAction = (l) => {
  if (l.status === "done" || l.delivered_at || l.order_state === "confirmed_dispatched" || l.quote_status === "approved")
    return { label: "Followup", tab: "delivery", cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/40 hover:bg-slate-500/25" };
  if (l.quote_status === "sent")
    return { label: "Quote Sent", tab: "quotation", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/50 hover:bg-emerald-500/25" };
  if (l.winning_vendor_id)
    return { label: "Quote Preparing", tab: "quotation", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/50 hover:bg-blue-500/25" };
  if (l.status === "quote_required" || l.rfq_sent)
    return { label: "Send RFQ", tab: "quotation", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/50 hover:bg-amber-500/25" };
  return { label: "Call Update", tab: "quotation", cls: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/50 hover:bg-red-500/25" };
};

export function WorkflowStatus({ lead, onOpen }) {
  const cur = currentStepIndex(lead);
  const total = WORKFLOW_STEPS.length;
  const complete = cur >= total;
  const action = leadAction(lead);
  const openAction = (e) => {
    e.stopPropagation();
    onOpen && onOpen(action.tab, { tab: action.tab });
  };
  const openStep = (e, stepObj) => {
    e.stopPropagation();
    onOpen && onOpen(stepObj.tab, stepObj);
  };
  return (
    <div className="min-w-[160px]" data-testid={`workflow-status-${lead.order_number}`}>
      <button
        type="button"
        onClick={openAction}
        className={`inline-flex items-center px-2.5 py-1 text-xs font-bold uppercase tracking-wide border transition-colors ${action.cls}`}
        data-testid={`lead-action-${lead.order_number}`}
        title="Open this task"
      >
        {action.label}
      </button>
      <div className="flex gap-0.5 mt-1.5">
        {WORKFLOW_STEPS.map((s, i) => {
          const cls = complete || i < cur ? "bg-emerald-500 hover:bg-emerald-400" : i === cur ? "bg-blue-500 hover:bg-blue-400" : "bg-muted-foreground/25 hover:bg-muted-foreground/50";
          return (
            <button
              key={s.key}
              type="button"
              onClick={(e) => openStep(e, s)}
              className={`h-2 flex-1 rounded-sm cursor-pointer transition-colors ${cls}`}
              title={`${i + 1}. ${s.label} — click to open`}
              data-testid={`workflow-seg-${lead.order_number}-${s.key}`}
            />
          );
        })}
      </div>
    </div>
  );
}
